import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  TIERS,
  loadTier,
  saveTier,
  resolveVariants,
  downloadAtTier,
  formatBytes,
  formatFailureReport,
} from './lib/downloadImages';
import './DownloadDialog.css';

/**
 * Quality picker plus byte-level progress, shared by every download entry point.
 *
 * images  - array of saved images ({ url, thumbnailUrl, fullSizeUrl, source, title })
 * onClose - called when dismissed, cancelled or finished
 * onReady - called with (files, setPercent, { failed, unresolved }) once every
 *           image is fetched. The caller decides what to do with them: a ZIP on
 *           desktop, the share sheet on mobile. May be async; setPercent drives
 *           the second bar. `failed` is the count that could not be fetched at
 *           all, so the caller can say so rather than silently delivering fewer
 *           files than the user asked for.
 */
function DownloadDialog({ images, onClose, onReady }) {
  const [tier, setTier] = useState(loadTier);
  const [sizes, setSizes] = useState({});
  const [phase, setPhase] = useState('choose'); // choose | downloading | finishing | report
  // Set only when images were skipped. The download itself has already been
  // delivered by the time this shows, so it is a report, not an error state.
  const [report, setReport] = useState(null);
  const [copied, setCopied] = useState(false);
  const [progress, setProgress] = useState({
    completed: 0,
    total: images.length,
    bytesLoaded: 0,
    bytesTotal: 0,
    bytesUnknown: 0,
    fraction: 0,
  });
  const [finishPercent, setFinishPercent] = useState(0);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);
  const probeAbortRef = useRef(null);
  const probedRef = useRef(new Set());

  // The image set is fixed for the lifetime of a dialog instance. Holding it in
  // a ref keeps the size probe from re-firing when a caller passes a fresh array
  // literal on every render.
  const imagesRef = useRef(images);

  // Probe the selected tier so its button can show what the download will cost.
  //
  // Only the selected one. Probing all three meant two thirds of every probe
  // asked about a quality the user never chose, and the whole batch fanned out
  // to the image hosts from one server address moments before the download did
  // the same again -- enough, in production, for those hosts to start refusing
  // us. Sizes are advisory, so a failed probe just leaves that tier blank.
  useEffect(() => {
    if (probedRef.current.has(tier)) return undefined;
    probedRef.current.add(tier);

    let cancelled = false;
    const controller = new AbortController();
    probeAbortRef.current = controller;

    resolveVariants(imagesRef.current, tier, controller.signal)
      .then((variants) => {
        const bytes = variants.reduce((sum, v) => sum + (v.bytes || 0), 0);
        const unknown = variants.filter((v) => v.bytes == null).length;
        if (!cancelled) setSizes((prev) => ({ ...prev, [tier]: { bytes, unknown } }));
      })
      .catch((err) => {
        // Forgetting the tier lets a probe that was cancelled mid-flight run
        // again if the user comes back to it.
        probedRef.current.delete(tier);
        // resolveVariants rethrows aborts, and start() aborts the probe the
        // moment the download begins. Recording that as a dash would tell the
        // user this tier cannot be sized, when all that happened is that we
        // stopped asking.
        if (cancelled || err.name === 'AbortError') return;
        setSizes((prev) => ({ ...prev, [tier]: null }));
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [tier]);

  // An in-flight download outlives the dialog otherwise: a route change or a
  // parent re-render that drops the dialog would leave the proxy streams running
  // and setProgress firing into an unmounted component.
  useEffect(() => () => {
    if (abortRef.current) abortRef.current.abort();
  }, []);

  const cancel = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    onClose();
  }, [onClose]);

  // Escape closes while choosing, and cancels an in-flight download.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (phase === 'choose' || phase === 'report') onClose();
      // Zipping is past the point of no return -- see the actions block below.
      else if (phase === 'downloading') cancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [phase, onClose, cancel]);

  const start = async () => {
    saveTier(tier);
    setPhase('downloading');
    setError(null);
    setFinishPercent(0);

    // The size probe has served its purpose; leaving it running competes with
    // the download for the same connection pool and rate-limit budget.
    if (probeAbortRef.current) probeAbortRef.current.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const { files, failed, unresolved, failures } = await downloadAtTier(images, tier, {
        signal: controller.signal,
        onProgress: setProgress,
      });

      if (files.length === 0) {
        setError('No images could be downloaded.');
        setPhase('choose');
        return;
      }

      setPhase('finishing');
      await onReady(files, setFinishPercent, { failed, unresolved });

      // The files are already delivered. Holding the dialog open on a report
      // rather than closing is the only way the user ever finds out *which*
      // images are missing from the ZIP they just received.
      if (failures && failures.length > 0) {
        setReport({ failures, tier, total: images.length, delivered: files.length });
        setPhase('report');
        return;
      }

      onClose();
    } catch (err) {
      if (err.name === 'AbortError') {
        onClose();
        return;
      }
      setError('Download failed. Try a lower quality.');
      setPhase('choose');
    }
  };

  // downloadAtTier guarantees fraction stays within 0..1 even when the server
  // could not size every image, so no clamping is needed here.
  const percent =
    phase === 'finishing'
      ? Math.round(finishPercent)
      : Math.round(progress.fraction * 100);

  const tierSize = (id) => {
    const size = sizes[id];
    // Undefined means unprobed: still being sized if this is the selected tier,
    // otherwise never asked about, and an unasked tier should say nothing
    // rather than sit on "sizing" forever. Null means the probe failed; a dash
    // used to cover both, which read as "this tier is empty".
    if (size === undefined) return id === tier ? 'sizing\u2026' : '';
    if (size === null) return '\u2014';
    // A partial total would read as precise, so say so when sizes are missing.
    if (size.unknown > 0 && size.bytes === 0) return 'size unknown';
    return size.unknown > 0 ? `~${formatBytes(size.bytes)}+` : formatBytes(size.bytes);
  };

  const copyReport = async () => {
    const text = formatFailureReport(report.failures, report.tier, report.total);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch (err) {
      // Clipboard access needs a secure context and, in some browsers, an
      // explicit permission. Falling back to a selectable textarea means the
      // report is never unreachable -- the user can still select and copy it.
      setCopied(false);
      window.prompt('Copy the report below', text);
    }
  };

  const choosing = phase === 'choose';
  const dismissable = choosing || phase === 'report';

  if (phase === 'report') {
    return (
      <div className="download-dialog-backdrop" onClick={onClose}>
        <div
          className="download-dialog download-dialog-wide"
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="download-dialog-title">
            Downloaded {report.delivered} of {report.total}
          </h2>

          <p className="download-report-summary">
            {report.failures.length} image{report.failures.length === 1 ? '' : 's'} could not be
            downloaded at {report.tier} quality.
          </p>

          <ul className="download-report-list">
            {report.failures.map((failure) => (
              <li key={failure.index} className="download-report-item">
                <div className="download-report-item-head">
                  <span className="download-report-item-title">{failure.title}</span>
                  <span className="download-report-item-source">{failure.source}</span>
                </div>
                {failure.attempts.length === 0 ? (
                  <div className="download-report-attempt">
                    no URLs to try (no full-size and no thumbnail stored)
                  </div>
                ) : (
                  failure.attempts.map((attempt) => (
                    <div key={attempt.url} className="download-report-attempt">
                      <span className="download-report-kind">{attempt.kind}</span>
                      <span className="download-report-reason">{attempt.reason}</span>
                      <span className="download-report-url" title={attempt.url}>
                        {attempt.url}
                      </span>
                    </div>
                  ))
                )}
              </li>
            ))}
          </ul>

          <div className="download-dialog-actions">
            <button type="button" className="download-dialog-button" onClick={copyReport}>
              {copied ? 'Copied' : 'Copy report'}
            </button>
            <button type="button" className="download-dialog-button primary" onClick={onClose}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="download-dialog-backdrop" onClick={dismissable ? onClose : undefined}>
      <div
        className="download-dialog"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        {choosing ? (
          <>
            <h2 className="download-dialog-title">
              Download {images.length} image{images.length === 1 ? '' : 's'}
            </h2>

            <div className="download-dialog-tiers">
              {TIERS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`download-tier ${tier === option.id ? 'selected' : ''}`}
                  aria-pressed={tier === option.id}
                  onClick={() => setTier(option.id)}
                >
                  <span className="download-tier-label">{option.label}</span>
                  <span className="download-tier-detail">{option.detail}</span>
                  <span className="download-tier-size">{tierSize(option.id)}</span>
                </button>
              ))}
            </div>

            {error && <p className="download-dialog-error">{error}</p>}

            <div className="download-dialog-actions">
              <button type="button" className="download-dialog-button" onClick={onClose}>
                Cancel
              </button>
              <button type="button" className="download-dialog-button primary" onClick={start}>
                Download
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="download-dialog-title">
              {phase === 'finishing' ? 'Preparing' : 'Downloading'}
            </h2>

            <div
              className="download-progress-track"
              role="progressbar"
              aria-valuenow={percent}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div className="download-progress-fill" style={{ width: `${percent}%` }} />
            </div>

            <p className="download-progress-label">
              {percent}%
              {phase === 'downloading' && (
                <>
                  {' \u00b7 '}
                  {progress.completed} of {progress.total}
                  {progress.bytesTotal > 0 && (
                    <>
                      {' \u00b7 '}
                      {formatBytes(progress.bytesLoaded)} / {formatBytes(progress.bytesTotal)}
                      {progress.bytesUnknown > 0 && '+'}
                    </>
                  )}
                </>
              )}
            </p>

            {/* Only offered while bytes are still being fetched. Once we are
                zipping or handing off to the share sheet the abort signal has
                nothing left to stop, so a Cancel button here would appear to
                work while the file downloaded anyway. */}
            {phase === 'downloading' && (
              <div className="download-dialog-actions">
                <button type="button" className="download-dialog-button" onClick={cancel}>
                  Cancel
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default DownloadDialog;
